import {
  Button,
  Checkbox,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { z } from 'zod';

import { attachAdGroup, getAllByAdvertiserId } from '../../api/ventura/product';
import { useConfig } from '../../hooks/useConfig';
import { camelCaseKeys, snakeCaseKeys } from '../../util/object';
import ProductSchema from '../../validationSchemas/ProductSchema';
import { headerStyles } from '../Contracts/ContractsTable';
import FormEndOptions from '../forms/FormEndOptions';

interface UnattachedProductsListProps {
  adGroupId: number;
  adGroupName: string;
  advertiserId: number;
  onClose?: Function;
  existingProductList: any;
}

function UnattachedProductsList(props: UnattachedProductsListProps) {
  const { adGroupId, adGroupName, advertiserId, existingProductList, onClose } = props;

  const { apiHost } = useConfig();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [selected, setSelected] = React.useState([]);

  const handlePageChange = (_event: unknown, value: number) => {
    setPage(value);
  };

  const handlePerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const query = useQuery({
    queryKey: ['products', advertiserId],
    queryFn: () => getAllByAdvertiserId(apiHost, advertiserId),
    select: camelCaseKeys,
  });

  const { data: productsList, isLoading } = query;

  const save = async () => {
    const promiseList = [];
    selected.forEach((productId) => {
      const product: z.infer<typeof ProductSchema> = {
        bidderCampaignId: adGroupId,
        venturaAdvertiserId: advertiserId,
        productId,
      };
      const payload = {
        product: snakeCaseKeys(product),
      };
      promiseList.push(attachAdGroup(apiHost, adGroupId, payload));
    });

    await Promise.all(promiseList);

    onClose();
  };

  const existingProductIds = existingProductList?.map((item) => item.productId);
  const unattachedProductsList = productsList?.collection?.filter(
    (product) => !existingProductIds?.includes(product.productId)
  );
  const totalItems = unattachedProductsList?.length || 0;
  const paginatedAdvertiserProductsList = unattachedProductsList?.slice(page * perPage, page * perPage + perPage - 1);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(paginatedAdvertiserProductsList?.map((product) => product.productId));
      return;
    }
    setSelected([]);
  };

  const headerStylesCss = headerStyles();

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Add Existing Products for {adGroupName}
        </Typography>
      </Stack>
      <TableContainer className={headerStylesCss.root}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead sx={{}}>
            <TableRow>
              <TableCell>
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < paginatedAdvertiserProductsList?.length}
                  checked={
                    productsList?.collection?.length > 0 && selected.length === paginatedAdvertiserProductsList?.length
                  }
                  onChange={handleSelectAllClick}
                />
              </TableCell>
              <TableCell>Name</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading &&
              Array.from({ length: perPage }, () => Math.random()).map((rowItem) => (
                <TableRow key={rowItem} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell scope="row" colSpan={100}>
                    <Skeleton animation="wave" variant="text" sx={{ fontSize: '1rem' }} />
                  </TableCell>
                </TableRow>
              ))}
            {paginatedAdvertiserProductsList?.map((product) => (
              <TableRow key={`link-${product.productId}`}>
                <TableCell scope="row">
                  <Checkbox
                    onClick={(event) => event.stopPropagation()}
                    checked={selected.indexOf(product.productId) !== -1}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelected([...selected, product.productId]);
                      } else {
                        setSelected(selected.filter((id) => id !== product.productId));
                      }
                    }}
                  />
                </TableCell>
                <TableCell scope="row">{product.name}</TableCell>
              </TableRow>
            ))}
            <TableRow key="pagination">
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                count={totalItems || 0}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={perPage}
                onRowsPerPageChange={handlePerPageChange}
              />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <FormEndOptions>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button variant="outlined" onClick={() => onClose()}>
            Close
          </Button>
          <Button onClick={() => save()}>Save</Button>
        </Stack>
      </FormEndOptions>
    </Stack>
  );
}

export default UnattachedProductsList;
